import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import { Typography, Box, Paper } from "@mui/material";
import { useSnackbar } from "notistack";
import { useRef, useState } from "react";
import {
  useUserPermissionsListQuery,
  useLazyUserPermissionsGetUserRolesQuery,
  useUserPermissionsSyncRolesMutation,
  useRoleListQuery,
} from "../api/api";
import { ConfigurableListItem } from "./ConfigurableListItem";
import { RelationsEditorModal } from "./RelationsEditorModal";
import { SearchField } from "./SearchField";
import { useListFilter } from "./useListFilter";

const bgColor = "#EDEDED";

export const EmployeesList = () => {
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: employeesData,
    isLoading,
    isError,
  } = useUserPermissionsListQuery();

  const {
    data: rolesData,
    isLoading: isRolesLoading,
    isError: isRolesError,
  } = useRoleListQuery();

  const [
    getUserRoles,
    { isLoading: isUserRolesLoading, isError: isUserRolesError },
  ] = useLazyUserPermissionsGetUserRolesQuery();

  const [syncRoles, { isLoading: isSyncRolesLoading }] =
    useUserPermissionsSyncRolesMutation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedUserName, setSelectedUserName] = useState("");
  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
  const userRolesRequestIdRef = useRef(0);

  const {
    search: employeeSearch,
    setSearch: setEmployeeSearch,
    clearSearch: clearEmployeeSearch,
    filteredItems: filteredEmployees,
    isFilteredEmpty: isEmployeesFilteredEmpty,
  } = useListFilter({
    items: employeesData?.data,
    getSearchValue: (employee) =>
      `${employee.first_name ?? ""} ${employee.last_name ?? ""}`,
  });

  const {
    search: roleSearch,
    setSearch: setRoleSearch,
    clearSearch: clearRoleSearch,
    filteredItems: filteredRoles,
    isFilteredEmpty: isRolesFilteredEmpty,
  } = useListFilter({
    items: rolesData?.data,
    getSearchValue: (role) => role.displayable_name || role.name,
  });

  const handleOpenModal = async (id: number) => {
    const requestId = ++userRolesRequestIdRef.current;
    const employee = employeesData?.data.find((item) => item.id === id);
    const employeeName = `${employee?.first_name ?? ""} ${
      employee?.last_name ?? ""
    }`.trim();

    setSelectedUserId(id);
    setSelectedUserName(employeeName);
    setIsModalOpen(true);
    setSelectedRoles([]);
    clearRoleSearch();

    try {
      const response = await getUserRoles(id).unwrap();

      if (requestId !== userRolesRequestIdRef.current) return;

      setSelectedRoles(response.data.roles.map((role) => role.id));
    } catch (error) {
      if (requestId !== userRolesRequestIdRef.current) return;

      enqueueSnackbar("Ошибка загрузки ролей пользователя", {
        variant: "error",
      });
    }
  };

  const handleCloseModal = () => {
    userRolesRequestIdRef.current += 1;
    setIsModalOpen(false);
    setSelectedUserId(null);
    setSelectedUserName("");
    setSelectedRoles([]);
    clearRoleSearch();
  };

  const handleToggleRole = (roleId: number) => {
    setSelectedRoles((prev) =>
      prev.includes(roleId)
        ? prev.filter((id) => id !== roleId)
        : [...prev, roleId],
    );
  };

  const handleSave = async () => {
    if (selectedUserId == null) return;

    try {
      await syncRoles({
        userId: selectedUserId,
        body: {
          roles: selectedRoles,
        },
      }).unwrap();

      enqueueSnackbar("Успех", {
        variant: "success",
      });

      handleCloseModal();
    } catch (error) {
      console.error("Ошибка сохранения ролей", error);

      enqueueSnackbar("Ошибка", {
        variant: "error",
      });
    }
  };

  if (isLoading) {
    return <Typography>Загрузка сотрудников...</Typography>;
  }

  if (isError) {
    return <Typography color="error">Ошибка загрузки сотрудников</Typography>;
  }

  return (
    <Box>
      <Typography
        variant="h6"
        sx={{
          mb: 7,
          fontSize: "1.25rem",
          fontWeight: 500,
          color: "text.secondary",
        }}
      >
        Сотрудники
      </Typography>

      <SearchField
        value={employeeSearch}
        onChange={setEmployeeSearch}
        onClear={clearEmployeeSearch}
        placeholder="Поиск юзера"
        sx={{ mb: 4 }}
      />

      <Paper
        elevation={3}
        sx={{
          p: 4.25,
          pr: 0,
          borderRadius: 3,
          bgcolor: "common.white",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            maxHeight: 615,
            overflowY: "auto",
            pr: 1,
          }}
        >
          {filteredEmployees.map((employee) => (
            <ConfigurableListItem
              key={employee.id}
              title={`${employee.first_name ?? ""} ${employee.last_name ?? ""}`}
              id={employee.id}
              onConfigure={handleOpenModal}
              color={bgColor}
            />
          ))}

          {isEmployeesFilteredEmpty && (
            <Typography color="text.secondary">
              Нет подходящих данных
            </Typography>
          )}
        </Box>
      </Paper>

      <RelationsEditorModal
        open={isModalOpen}
        title="Роли"
        entityTitle={
          selectedUserName || `Пользователь #${selectedUserId ?? ""}`
        }
        relationTitle="Роли"
        relationIcon={
          <ManageAccountsOutlinedIcon
            sx={{ fontSize: 36, color: "text.secondary" }}
          />
        }
        items={filteredRoles}
        selectedIds={selectedRoles}
        searchValue={roleSearch}
        searchPlaceholder="Поиск роли"
        isFilteredEmpty={isRolesFilteredEmpty}
        isLoading={isUserRolesLoading || isRolesLoading}
        isError={isUserRolesError || isRolesError}
        isSaving={isSyncRolesLoading}
        loadingText="Загрузка ролей..."
        errorText="Ошибка загрузки ролей"
        getItemLabel={(role) => role.displayable_name || role.name}
        onSearchChange={setRoleSearch}
        onSearchClear={clearRoleSearch}
        onToggle={handleToggleRole}
        onSave={handleSave}
        onClose={handleCloseModal}
      />
    </Box>
  );
};
